import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, MessageSquare, Settings2, Trash2, ChevronRight, ChevronDown,
  Monitor, Smartphone, Tablet, User, Eye, EyeOff, Save, RefreshCw,
  Globe, Clock, Hash, Shield, CheckCircle, AlertCircle, Loader2,
} from 'lucide-react';

/* ── API helpers ─────────────────────────────────────────────────────────── */
const API = '/cms-api/chatbot.php';
const apiFetch = (url: string, opts?: RequestInit) =>
  fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts }).then(r => r.json());

/* ── Types ───────────────────────────────────────────────────────────────── */
interface ChatMessage { role: 'user' | 'assistant'; content: string; ts: number; }
interface ChatSession {
  id: string; sessionId: string; ip: string; fingerprint: string;
  userEmail?: string; browser: string; os: string; deviceType: string;
  screenRes: string; timezone: string; lang: string; userAgent: string;
  startedAt: string; lastActivity: string; messagesCount: number;
  messages: ChatMessage[];
}
interface ChatConfig {
  openaiKeyMasked: string; openaiKeySet: boolean; model: string;
}

/* ── Formatare data ──────────────────────────────────────────────────────── */
function fmtDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('ro-RO') + ' ' + d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}
function fmtTime(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/* ── Device icon ─────────────────────────────────────────────────────────── */
function DeviceIcon({ type }: { type: string }) {
  if (type === 'mobile') return <Smartphone size={14} className="text-[#4ab8ff]" />;
  if (type === 'tablet') return <Tablet size={14} className="text-[#4ab8ff]" />;
  return <Monitor size={14} className="text-[#4ab8ff]" />;
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB 1 — INTERACȚIUNI
════════════════════════════════════════════════════════════════════════════ */
function LogsTab() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiFetch(`${API}?action=logs`);
    setSessions(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSession = async (sessionId: string) => {
    if (!confirm('Ștergi această sesiune?')) return;
    setDeleting(sessionId);
    await apiFetch(API, { method: 'DELETE', body: JSON.stringify({ sessionId }) });
    setSessions(s => s.filter(x => x.sessionId !== sessionId));
    setDeleting(null);
    if (expanded === sessionId) setExpanded(null);
  };

  const filtered = sessions.filter(s =>
    !search || [s.ip, s.userEmail, s.browser, s.os, s.userAgent]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Caută după IP, email, browser, OS..."
          className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 outline-none focus:border-[#295CA8]"
        />
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#295CA8]/20 border border-[#295CA8]/30 text-[#4ab8ff] text-sm hover:bg-[#295CA8]/30 transition-all disabled:opacity-40">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Reîmprospătare
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total sesiuni', value: sessions.length, icon: MessageSquare },
          { label: 'Mesaje totale', value: sessions.reduce((a, s) => a + (s.messagesCount || 0), 0), icon: Hash },
          { label: 'Cu email', value: sessions.filter(s => s.userEmail).length, icon: User },
          { label: 'Azi', value: sessions.filter(s => s.startedAt?.startsWith(new Date().toISOString().slice(0,10))).length, icon: Clock },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(41,92,168,0.08)', border: '1px solid rgba(74,184,255,0.1)' }}>
            <Icon size={18} className="mx-auto mb-1.5 text-[#4ab8ff]/60" />
            <div className="text-2xl font-bold text-white">{value}</div>
            <div className="text-xs text-white/40 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Tabel */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="text-[#4ab8ff] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <Bot size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nicio interacțiune înregistrată</p>
          <p className="text-xs mt-1">Conversațiile vor apărea aici după ce utilizatorii folosesc chatbot-ul</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => (
            <motion.div key={s.sessionId} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-xl overflow-hidden"
              style={{ background: '#07090f', border: '1px solid rgba(255,255,255,0.07)' }}>

              {/* Row header */}
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/3 transition-colors"
                onClick={() => setExpanded(expanded === s.sessionId ? null : s.sessionId)}>

                <DeviceIcon type={s.deviceType} />

                {/* IP + Fingerprint */}
                <div className="w-32 flex-shrink-0">
                  <div className="text-sm font-mono text-white/80">{s.ip}</div>
                  <div className="text-[10px] text-white/25 font-mono truncate" title={s.fingerprint}>
                    #{s.fingerprint?.slice(0,8)}
                  </div>
                </div>

                {/* Email utilizator */}
                <div className="w-44 flex-shrink-0 min-w-0">
                  {s.userEmail ? (
                    <div className="flex items-center gap-1.5">
                      <User size={11} className="text-green-400 flex-shrink-0" />
                      <span className="text-xs text-green-300/80 truncate">{s.userEmail}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-white/25 italic">Anonim</span>
                  )}
                </div>

                {/* Browser + OS */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white/60 truncate">{s.browser} · {s.os}</div>
                  <div className="text-[10px] text-white/25 truncate">{s.screenRes} · {s.timezone}</div>
                </div>

                {/* MAC Address info */}
                <div className="hidden md:block w-32 flex-shrink-0">
                  <div className="text-[10px] text-white/25 flex items-center gap-1">
                    <Shield size={9} />
                    <span>MAC indisponibil</span>
                  </div>
                  <div className="text-[10px] text-white/20">(restricție browser)</div>
                </div>

                {/* Date + mesaje */}
                <div className="text-right flex-shrink-0 w-28">
                  <div className="text-xs text-white/50">{fmtDate(s.lastActivity)}</div>
                  <div className="text-[10px] text-[#4ab8ff]/60">{s.messagesCount || 0} mesaje</div>
                </div>

                {/* Expand + Delete */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); deleteSession(s.sessionId); }}
                    disabled={deleting === s.sessionId}
                    className="p-1.5 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-all">
                    {deleting === s.sessionId ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                  {expanded === s.sessionId
                    ? <ChevronDown size={14} className="text-[#4ab8ff]" />
                    : <ChevronRight size={14} className="text-white/30" />}
                </div>
              </div>

              {/* Drill-down: conversație */}
              <AnimatePresence>
                {expanded === s.sessionId && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
                    className="overflow-hidden">
                    <div className="px-4 pb-4 pt-0 border-t border-white/5">

                      {/* Device details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 mt-3">
                        {[
                          { label: 'IP Origine', value: s.ip },
                          { label: 'Browser', value: `${s.browser}` },
                          { label: 'Sistem Operare', value: s.os },
                          { label: 'Rezoluție', value: s.screenRes },
                          { label: 'Timezone', value: s.timezone },
                          { label: 'Limbă', value: s.lang },
                          { label: 'Device Fingerprint', value: s.fingerprint?.slice(0,16) + '…' },
                          { label: 'Email cont', value: s.userEmail || 'Neidentificat' },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <div className="text-[9px] text-white/30 uppercase tracking-wide">{label}</div>
                            <div className="text-xs text-white/70 mt-0.5 truncate" title={value}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* MAC address info */}
                      <div className="flex items-start gap-2 mb-4 px-3 py-2 rounded-lg text-xs"
                        style={{ background: 'rgba(250,204,21,0.06)', border: '1px solid rgba(250,204,21,0.12)' }}>
                        <Shield size={12} className="text-yellow-400/60 mt-0.5 flex-shrink-0" />
                        <span className="text-yellow-300/50">
                          <b className="text-yellow-300/70">MAC Address:</b> Adresa MAC nu este accesibilă prin browser (restricție de securitate W3C/OS). În locul ei este disponibil fingerprint-ul de device (<code className="text-yellow-300/50 text-[10px]">{s.fingerprint}</code>) bazat pe IP + User-Agent + rezoluție + timezone.
                        </span>
                      </div>

                      {/* Conversație */}
                      <div className="text-xs text-white/30 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <MessageSquare size={11} /> Istoricul conversației
                      </div>
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1"
                        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(74,184,255,0.15) transparent' }}>
                        {(s.messages || []).map((msg, i) => (
                          <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              msg.role === 'assistant' ? 'bg-gradient-to-br from-[#295CA8] to-[#4ab8ff]' : 'bg-white/10'
                            }`}>
                              {msg.role === 'assistant' ? <Bot size={10} className="text-white" /> : <User size={9} className="text-white/70" />}
                            </div>
                            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                              msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'
                            }`} style={msg.role === 'user'
                              ? { background: 'linear-gradient(135deg,rgba(41,92,168,0.5),rgba(61,114,184,0.5))', border: '1px solid rgba(74,184,255,0.15)' }
                              : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                              <div className="text-white/80">{msg.content}</div>
                              {msg.ts > 0 && <div className="text-white/25 text-[9px] mt-1">{fmtTime(msg.ts)}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   TAB 2 — CONFIGURARE
════════════════════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════════════════════
   TAB 2 — CONFIGURARE  (localStorage-based, fără PHP)
════════════════════════════════════════════════════════════════════════════ */
// OpenAI Responses API endpoint
const OPENAI_TEST_URL = 'https://api.openai.com/v1/responses';

function ConfigTab() {
  const [apiKey, setApiKey]       = useState('');
  const [model,  setModel]        = useState('gpt-4o-mini');
  const [showKey, setShowKey]     = useState(false);
  const [status, setStatus]       = useState<'idle'|'testing'|'ok'|'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [saved, setSaved]         = useState(false);

  /* ── Inițializare din localStorage ── */
  useEffect(() => {
    const storedKey   = localStorage.getItem('interpersonal_openai_key') || '';
    const storedModel = localStorage.getItem('interpersonal_chatbot_model') || 'gpt-5.4-mini';
    setApiKey(storedKey);
    setModel(storedModel);
    if (storedKey) {
      setStatus('idle');
    }
  }, []);

  /* ── Salvare ── */
  const save = () => {
    if (apiKey.trim()) {
      localStorage.setItem('interpersonal_openai_key', apiKey.trim());
    } else {
      localStorage.removeItem('interpersonal_openai_key');
    }
    localStorage.setItem('interpersonal_chatbot_model', model);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    // resetăm statusul după salvare pentru ca userul să retesteze
    setStatus('idle');
    setStatusMsg('');
  };

  /* ── Test conexiune ── */
  const testConnection = async () => {
    const key = apiKey.trim();
    if (!key) {
      setStatus('error');
      setStatusMsg('Introduceți cheia API înainte de test.');
      return;
    }
    setStatus('testing');
    setStatusMsg('');
    try {
      const res = await fetch(OPENAI_TEST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${key}`,
        },
        body: JSON.stringify({
          model,
          input: 'ping',
          store: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.error?.message || `Eroare HTTP ${res.status}`;
        setStatus('error');
        setStatusMsg(msg);
      } else {
        setStatus('ok');
        setStatusMsg(`Model: ${data?.model || model} · OK`);
      }
    } catch (e: unknown) {
      setStatus('error');
      setStatusMsg(e instanceof Error ? e.message : 'Eroare de rețea');
    }
  };

  const statusUI = {
    idle:    { label: 'Neconfigurat',    color: 'text-white/40',   bg: 'rgba(255,255,255,0.05)',   dot: 'bg-white/20',    glow: '' },
    testing: { label: 'Se testează...',  color: 'text-yellow-300', bg: 'rgba(253,224,71,0.08)',    dot: 'bg-yellow-400',  glow: '0 0 10px rgba(253,224,71,0.4)' },
    ok:      { label: 'Activ',           color: 'text-green-400',  bg: 'rgba(74,222,128,0.08)',    dot: 'bg-green-400',   glow: '0 0 14px rgba(74,222,128,0.7)' },
    error:   { label: 'Deconectat',      color: 'text-red-400',    bg: 'rgba(248,113,113,0.08)',   dot: 'bg-red-400',     glow: '0 0 14px rgba(248,113,113,0.7)' },
  }[status];

  return (
    <div className="max-w-2xl space-y-6">

      {/* ── Card status ───────────────────────────────────────────────── */}
      <div className="rounded-xl p-5"
        style={{ background: statusUI.bg, border: `1px solid rgba(74,184,255,0.15)` }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)' }}>
            <Bot size={17} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white/40 uppercase tracking-wide">Stare ChatBot AI</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusUI.dot}`}
                style={{ boxShadow: statusUI.glow }} />
              <span className={`text-sm font-bold ${statusUI.color}`}>{statusUI.label}</span>
              {status === 'testing' && <Loader2 size={13} className="text-yellow-300 animate-spin" />}
            </div>
            {statusMsg && (
              <p className={`text-xs mt-1 ${status === 'ok' ? 'text-green-300/70' : 'text-red-300/70'}`}>
                {statusMsg}
              </p>
            )}
          </div>
          <button onClick={testConnection} disabled={status === 'testing'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
            style={{ background: 'rgba(41,92,168,0.3)', border: '1px solid rgba(74,184,255,0.2)', color: '#4ab8ff' }}>
            <RefreshCw size={12} className={status === 'testing' ? 'animate-spin' : ''} />
            Testează
          </button>
        </div>
      </div>

      {/* ── Card configurare ──────────────────────────────────────────── */}
      <div className="rounded-xl p-5 space-y-5"
        style={{ background: 'rgba(41,92,168,0.08)', border: '1px solid rgba(74,184,255,0.15)' }}>

        <h3 className="text-sm font-bold text-white">Configurare OpenAI</h3>

        {/* Cheie API */}
        <div>
          <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide">
            Cheie API OpenAI <span className="text-red-400">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => { setApiKey(e.target.value); setStatus('idle'); setStatusMsg(''); }}
              placeholder="sk-proj-..."
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono text-white placeholder-white/20 outline-none focus:border-[#295CA8] transition-colors"
            />
            <button onClick={() => setShowKey(v => !v)}
              className="px-3 rounded-lg bg-white/5 border border-white/10 text-white/40 hover:text-white transition-colors">
              {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <p className="text-[10px] text-white/25 mt-1">
            Cheia este stocată local în browser (localStorage). Nu se trimite pe server.
          </p>
        </div>

        {/* Model GPT */}
        <div>
          <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide">Model GPT</label>
          <select value={model} onChange={e => setModel(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#295CA8] transition-colors">
            <option value="gpt-5.4-mini">gpt-5.4-mini (rapid, economic — recomandat)</option>
            <option value="gpt-4o-mini">gpt-4o-mini</option>
            <option value="gpt-4o">gpt-4o (performanță maximă)</option>
            <option value="gpt-3.5-turbo">gpt-3.5-turbo (legacy)</option>
          </select>
        </div>

        {/* Butoane */}
        <div className="flex gap-3">
          <button onClick={save}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
            style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)', color: '#fff' }}>
            {saved ? <CheckCircle size={15} /> : <Save size={15} />}
            {saved ? 'Salvat!' : 'Salvează configurarea'}
          </button>
          <button onClick={testConnection} disabled={status === 'testing' || !apiKey.trim()}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-40"
            style={{ background: 'rgba(74,184,255,0.1)', border: '1px solid rgba(74,184,255,0.25)', color: '#4ab8ff' }}>
            <RefreshCw size={15} className={status === 'testing' ? 'animate-spin' : ''} />
            Test
          </button>
        </div>
      </div>

      {/* ── Info bloc ─────────────────────────────────────────────────── */}
      <div className="rounded-xl p-4 space-y-2 text-xs text-white/40"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <p className="font-semibold text-white/60 flex items-center gap-1.5"><Globe size={12} /> Cum funcționează</p>
        <p>• Chatbot-ul de pe site apelează direct <code className="text-[#4ab8ff]/60">OpenAI Responses API</code> din browser (fără proxy PHP)</p>
        <p>• Cheia este stocată în <code className="text-[#4ab8ff]/60">localStorage</code> — confidențial, nu se trimite la server</p>
        <p>• Modelul selectat este aplicat la fiecare conversație nouă pe site</p>
        <p>• Chatbot-ul răspunde exclusiv pe baza informațiilor despre Interpersonal S.R.L.</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   PAGINA PRINCIPALĂ
════════════════════════════════════════════════════════════════════════════ */
export default function ChatBotPage() {
  const [activeTab, setActiveTab] = useState<'logs' | 'config'>('logs');

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#295CA8,#4ab8ff)', boxShadow: '0 0 20px rgba(74,184,255,0.3)' }}>
          <Bot size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Chat Bot AI</h1>
          <p className="text-sm text-white/40">Monitorizare interacțiuni · Configurare OpenAI</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {([
          ['logs',   MessageSquare, 'Interacțiuni utilizatori'],
          ['config', Settings2,    'Configurare'],
        ] as const).map(([t, Icon, label]) => (
          <button key={t} onClick={() => setActiveTab(t as 'logs'|'config')}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === t ? 'text-[#4ab8ff] border-[#295CA8]' : 'text-white/40 border-transparent hover:text-white'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'logs'   && <LogsTab />}
      {activeTab === 'config' && <ConfigTab />}
    </div>
  );
}
